/**
 * aethereum goal — drive the team's live loop objective from the terminal.
 *
 * A goal is the loop's north star: an objective, an optional iteration/token
 * budget, and a stop-condition, pinned atop every agent's get_team_context under
 * "== ACTIVE GOAL ==". The built-in governor prints "⚠ Budget exhausted. STOP
 * iterating" once the budget is hit. Goals were MCP-tool only; this wraps the
 * same hosted command bridge so the loop (and cron) can be driven from a shell.
 * `set` writes a new goal, `update` advances it, `show` renders the active one.
 */

import { resolveHostedConfig, type HostedFlags } from "./hosted-config.js";
import { HostedWriteClient, type ActiveGoal } from "../hive/hosted-client.js";

export interface GoalOptions extends HostedFlags {
  env?: { AETHEREUM_SYNC_API_URL?: string; AETHEREUM_SYNC_TOKEN?: string };
  fetchImpl?: typeof fetch;
}

const STATUSES = ["active", "blocked", "done", "abandoned"] as const;

/** Parse an integer flag; undefined when absent or unparseable (never NaN). */
function intFlag(raw: string | undefined): number | undefined {
  if (raw === undefined) return undefined;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) ? n : undefined;
}

function shortId(id: string): string {
  return id.length > 8 ? id.slice(0, 8) : id;
}

function renderGoal(g: ActiveGoal | null): string {
  if (!g) return "No active goal.";
  const lines = [`#${shortId(g.goalId)}  [${g.status}]  ${g.text}`];
  const burn: string[] = [];
  if (g.maxIterations !== undefined || g.iteration !== undefined) {
    burn.push(`iteration ${g.iteration ?? 0}${g.maxIterations !== undefined ? `/${g.maxIterations}` : ""}`);
  }
  if (g.budgetTokens !== undefined || g.spentTokens !== undefined) {
    burn.push(`tokens ${g.spentTokens ?? 0}${g.budgetTokens !== undefined ? `/${g.budgetTokens}` : ""}`);
  }
  if (burn.length) lines.push(`  budget: ${burn.join(" · ")}`);
  if (g.stopCondition) lines.push(`  stop when: ${g.stopCondition}`);
  if (g.by) lines.push(`  — set by ${g.by}`);
  const overIter = g.maxIterations !== undefined && (g.iteration ?? 0) >= g.maxIterations;
  const overTokens = g.budgetTokens !== undefined && (g.spentTokens ?? 0) >= g.budgetTokens;
  if (overIter || overTokens) {
    lines.push("  ⚠ Budget exhausted. STOP iterating: mark the goal done/blocked instead of looping.");
  }
  return lines.join("\n");
}

export async function runGoal(args: string[], options: GoalOptions = {}): Promise<void> {
  const sub = args[0];
  const rest = args.slice(1);

  const cfg = resolveHostedConfig(options, options.env ?? process.env);
  if ("error" in cfg) {
    console.error(cfg.error);
    process.exit(1);
    return;
  }

  const client = new HostedWriteClient(cfg.baseUrl, cfg.token, options.fetchImpl ?? fetch);

  // local flag reader (operates over `rest` so positionals stay clean)
  const flag = (name: string): string | undefined => {
    const idx = rest.indexOf(`--${name}`);
    if (idx === -1) return undefined;
    const v = rest[idx + 1];
    if (v === undefined || v.startsWith("--")) return undefined;
    return v;
  };
  // positional args = everything before the first flag
  const firstFlag = rest.findIndex((a) => a.startsWith("--"));
  const positionals = firstFlag === -1 ? rest : rest.slice(0, firstFlag);

  try {
    switch (sub) {
      case "set": {
        const text = positionals.join(" ").trim();
        if (!text) {
          console.error(
            'Usage: aethereum goal set "<text>" [--max-iterations N] [--budget-tokens N] [--stop "<cond>"]',
          );
          process.exit(1);
          return;
        }
        const { goalId } = await client.setGoal({
          text,
          maxIterations: intFlag(flag("max-iterations")),
          budgetTokens: intFlag(flag("budget-tokens")),
          stopCondition: flag("stop"),
        });
        console.log(`✓ goal set #${shortId(goalId)} — pinned atop every agent's context`);
        return;
      }

      case "update": {
        const [goalId] = positionals;
        if (!goalId) {
          console.error(
            "Usage: aethereum goal update <goalId> [--status active|blocked|done|abandoned] [--iteration N] [--spent-tokens N]",
          );
          process.exit(1);
          return;
        }
        const statusRaw = flag("status");
        const status = STATUSES.includes(statusRaw as (typeof STATUSES)[number])
          ? (statusRaw as (typeof STATUSES)[number])
          : undefined;
        await client.updateGoal({
          goalId,
          status,
          iteration: intFlag(flag("iteration")),
          spentTokens: intFlag(flag("spent-tokens")),
        });
        console.log(`✓ goal #${shortId(goalId)} updated${status ? ` → ${status}` : ""}`);
        return;
      }

      case "show": {
        console.log(renderGoal(await client.fetchActiveGoal()));
        return;
      }

      default:
        console.error(
          'Usage: aethereum goal set "<text>" [...] | goal update <goalId> [...] | goal show',
        );
        process.exit(1);
    }
  } catch (err) {
    console.error(err instanceof Error ? err.message : String(err));
    process.exit(1);
  }
}
